import { isUnassignedShelf, shelfLabel, stockIntegritySummary } from '../inventoryView';
import type { InventoryScreenRow } from '../inventoryView';
import { locationStock } from '../locationStock';

export interface LocationStockCardProps {
  /** Every row the screen holds; the card picks the location's own. */
  rows: InventoryScreenRow[];
  /** The location the search names exactly, scanned or typed. */
  locationCode: string;
  /**
   * Whether the rows include those at 0 on hand. Without them a reservation left on an emptied row is not read at all,
   * so Reserved can fall short until they are read.
   */
  emptiesRead: boolean;
  /** Reads the rows at 0 on hand too; a read, never a write. */
  onReadEmpties: () => void;
}

/**
 * What one location physically holds, for a count at that location [SSD-7553]. Read-only: it adds up every row on the
 * location whatever the Line, Sourcing route, zero-quantity or audit filters leave in the table, because a count finds
 * the whole shelf; and it never adds a row of the same sku on another location.
 */
export function LocationStockCard({ rows, locationCode, emptiesRead, onReadEmpties }: LocationStockCardProps) {
  const stock = locationStock(rows, locationCode);
  const label = shelfLabel(locationCode);
  const issues = stockIntegritySummary(stock.rows);
  return (
    <section className="card loc-stock" aria-label={`Stock at ${label}`}>
      <h4>
        Stock at <span className="loc-pill">{label}</span>
      </h4>
      <div className="bigrow">
        <div className="bignum">
          <div className="n">{stock.rows.length}</div>
          <div className="l">SKUs</div>
        </div>
        <div className="bignum">
          <div className="n">{stock.onHand}</div>
          <div className="l">On Hand (physical)</div>
        </div>
        <div className="bignum">
          <div className="n res">{stock.reserved}</div>
          <div className="l">Reserved (part of On Hand)</div>
        </div>
        <div className="bignum">
          <div className={`n ${stock.available < 0 ? 'text-danger' : 'av'}`}>{stock.available}</div>
          <div className="l">Available</div>
        </div>
      </div>
      <p className="loc-stock-note">
        On Hand is what this whole location should physically hold — every row on it, whatever the filters below show.
        Reserved and Available split that same quantity: reserved units are not extra stock, and they stay counted here
        until their order is outbounded, even once picked for packing.
      </p>
      {!emptiesRead && (
        <p className="loc-stock-note">
          Rows at 0 on hand are not read, so a reservation left on an emptied row is not in Reserved yet.{' '}
          <button type="button" className="btn btn-line btn-sm" onClick={onReadEmpties}>
            Include emptied rows
          </button>
        </p>
      )}
      {isUnassignedShelf(locationCode) && (
        <p className="loc-stock-note">Unassigned stock has no shelf recorded, so it cannot be counted at one place.</p>
      )}
      {issues && (
        <div role="status" className="note warn">
          {`This location does not add up: ${issues}.`}
        </div>
      )}
    </section>
  );
}
